import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";

const LOCATIONS = ["All", "Goa", "Bangalore"];
const TYPES = ["All", "Villa", "Apartment", "Guest House", "Cottage"];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wishlistIds, setWishlistIds] = useState(new Set());

  const location = params.get("location") || "All";
  const type = params.get("type") || "All";
  const guests = params.get("guests") || "";
  const priceMax = Number(params.get("max_price") || 30000);
  const q = params.get("q") || "";

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v === undefined || v === null || v === "" || v === "All") next.delete(k);
    else next.set(k, String(v));
    setParams(next, { replace: true });
  };

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (location && location !== "All") p.set("location", location);
    if (type && type !== "All") p.set("property_type", type);
    if (guests) p.set("guests", guests);
    if (priceMax) p.set("max_price", String(priceMax));
    if (q) p.set("q", q);
    return p.toString();
  }, [location, type, guests, priceMax, q]);

  useEffect(() => {
    setLoading(true);
    api.get(`/properties?${queryString}`)
      .then((r) => setProperties(r.data))
      .finally(() => setLoading(false));
  }, [queryString]);

  useEffect(() => {
    api.get("/wishlist").then((r) => setWishlistIds(new Set(r.data.property_ids || []))).catch(() => {});
  }, []);

  const handleWishlistChange = (id, inList) => {
    setWishlistIds((prev) => {
      const next = new Set(prev);
      if (inList) next.add(id); else next.delete(id);
      return next;
    });
  };

  return (
    <div className="mx-auto max-w-7xl px-6 md:px-10 py-12" data-testid="search-page">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.25em] text-[hsl(10_51%_55%)]">Search results</p>
        <h1 className="font-serif-display text-3xl sm:text-4xl mt-2">
          {location === "All" ? "All stays" : `Stays in ${location}`}
        </h1>
      </div>

      {/* Filters */}
      <div className="mb-10 grid grid-cols-1 md:grid-cols-4 gap-6 items-start" data-testid="filters-section">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[hsl(100_3%_30%)]">Search</label>
          <Input
            value={q}
            onChange={(e) => setParam("q", e.target.value)}
            placeholder="Search title, address..."
            className="mt-2"
            data-testid="filter-search"
          />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[hsl(100_3%_30%)] mb-2">Destination</div>
          <div className="flex gap-2 flex-wrap no-scrollbar">
            {LOCATIONS.map((l) => (
              <button
                key={l}
                onClick={() => setParam("location", l)}
                className={`chip ${location === l ? "chip-active" : ""}`}
                data-testid={`filter-location-${l.toLowerCase()}`}
              >{l}</button>
            ))}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[hsl(100_3%_30%)] mb-2">Type</div>
          <div className="flex gap-2 flex-wrap">
            {TYPES.map((t) => (
              <button
                key={t}
                onClick={() => setParam("type", t)}
                className={`chip ${type === t ? "chip-active" : ""}`}
                data-testid={`filter-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
              >{t}</button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[hsl(100_3%_30%)]">
            Max price per night · ₹{priceMax.toLocaleString("en-IN")}
          </label>
          <Slider
            min={2000}
            max={30000}
            step={500}
            value={[priceMax]}
            onValueChange={(v) => setParam("max_price", v[0])}
            className="mt-4"
            data-testid="filter-price"
          />
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-center py-24 text-[hsl(100_3%_30%)]">Loading stays…</div>
      ) : properties.length === 0 ? (
        <div className="text-center py-24" data-testid="no-results">
          <p className="font-serif-display text-2xl mb-2">No stays match those filters.</p>
          <p className="text-[hsl(100_3%_30%)]">Try widening your dates or price range.</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-[hsl(100_3%_30%)] mb-6" data-testid="result-count">{properties.length} homes</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {properties.map((p) => (
              <PropertyCard key={p.id} property={p} isWishlisted={wishlistIds.has(p.id)} onWishlistChange={handleWishlistChange} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
